import React, { useState, useEffect } from 'react';
import { CheckboxCard, ListItemLayout, ListLayout, Pager, SearchBar, DropdownSearch, DropdownOption, DropdownAsync } from '@keplerco/core';
import classNames from 'classnames';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { SelectionEntity } from '../../../../components/panels/selection/selection.models';
import { useAppActions, useAppState } from '../../../../overmind';
import { SortField, ActivationStatus, OrganizationLevelType } from '../../../../enums';
import { Department } from '../../../../models/view/department';
import { TeamSearchResponse } from '../../../../models';
import { MAX_PAGE_SIZE } from '../../../../library/consts/consts';
import { extractHighestOrganizationLevel } from '../../../../library/helpers/permissions/extract-highest-organization-level';
import wizardStyles from '../../assessments-wizard.module.css';
import selectionPanelStyles from '../../../../components/panels/selection/selection.module.css';
import { PeerAssessmentAssigneeResponse } from '../../../../overmind/actions';
import { PeopleSearchRequest } from '../../../../models/overmind/search-request';
import { AssessmentAssigneeListItemLayout } from '../../../../components/assessment/assessment-entity-list-item.layout';

export function SubjectSelectionList({ selectedEntities, setSelectedEntities }: { selectedEntities: SelectionEntity<PeerAssessmentAssigneeResponse>[]; setSelectedEntities: (selectedEntities: SelectionEntity<PeerAssessmentAssigneeResponse>[]) => void }): React.ReactElement {
  const actions = useAppActions();
  const { companyVariables, permissions, user } = useAppState();
  const organizationLevel: OrganizationLevelType = React.useMemo(
    () => (user?.isSystemAdmin ? OrganizationLevelType.Company : extractHighestOrganizationLevel(permissions?.analytics.organizationLevels)?.organizationLevel ?? OrganizationLevelType.Learner),
    [user?.isSystemAdmin, permissions?.analytics.organizationLevels]
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [request, setRequest] = useState<PeopleSearchRequest>({
    organizationLevel,
    companySlug: companyVariables.slug!,
    sortField: SortField.Name,
    sortAscending: true,
    page: 1,
    pageSize: 5,
    activationStatuses: [ActivationStatus.Active, ActivationStatus.Inactive, ActivationStatus.NotInvited],
  });
  const [departmentOptions, setDepartmentOptions] = useState<DropdownOption[]>([]);
  const [teamOptions, setTeamOptions] = useState<DropdownOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);
  const [isSearchingRole, setIsSearchingRole] = useState<boolean>(false);
  const [selectedRoleOptions, setSelectedRoleOptions] = useState<DropdownOption[]>([]);
  const [people, setPeople] = useState<PeerAssessmentAssigneeResponse[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);

  async function getTeamOptions(request: PeopleSearchRequest) {
    const response: TeamSearchResponse | undefined = await actions.searchTeams({
      organizationLevel,
      companySlug: companyVariables.slug!,
      departmentSlug: request.departmentSlug,
      sortField: SortField.Name,
      sortAscending: true,
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    });
    setTeamOptions((response?.teams || []).map(team => ({ label: team.teamName, value: team.teamSlug })));
  }

  async function getPeople(request: PeopleSearchRequest) {
    const response = await actions.searchPeople(request);
    setPeople(
      response?.employees.map(employee => ({
        learnerSlug: employee.learnerSlug,
        fullName: `${employee.firstName} ${employee.lastName}`,
        department: employee.department?.name,
        team: employee.team?.name,
        roles: employee.roles ?? [],
        email: employee.email,
        reviewers: [],
        companySkills: [],
      })) ?? []
    );
    setTotalPages(response?.totalPages ?? 1);
  }

  async function searchRoleOptions(search: string) {
    setIsSearchingRole(true);
    const response = await actions.getCompanyRoles({
      companySlug: companyVariables.slug!,
      search,
      sortAscending: true,
      page: 1,
      pageSize: 10,
      searchGlobally: false,
    });
    setRoleOptions(response?.roles.map(role => ({ label: role.name, value: role.slug! })) ?? []);
    setIsSearchingRole(false);
  }

  useEffect(() => {
    async function getDepartmentOptions() {
      if (!!departmentOptions.length) return;
      const response: Department[] | undefined = await actions.getDepartments(companyVariables.slug!);
      setDepartmentOptions((response || []).map(department => ({ label: department.name, value: department.slug })));
    }

    async function initialize() {
      setIsLoading(true);
      await getDepartmentOptions();
      await getTeamOptions(request);
      await getPeople(request);
      setIsLoading(false);
    }

    initialize();
  }, []);

  function handlePersonSelection(person: PeerAssessmentAssigneeResponse, isSelected: boolean) {
    if (isSelected) {
      setSelectedEntities([...selectedEntities, { slug: person.learnerSlug, name: person.fullName, entity: person }]);
    } else {
      setSelectedEntities(selectedEntities.filter(selectedEntity => selectedEntity.slug !== person.learnerSlug));
    }
  }

  return (
    <SkeletonOverlay isLoading={isLoading} width="100%">
      <div className={selectionPanelStyles.selectionList}>
        <SearchBar
          label="Search"
          value={request.search}
          responsive
          loading={isSearching}
          onInput={async event => {
            const search = (event.target as HTMLInputElement).value;
            if (search === request.search) return;
            setIsSearching(true);
            const nextRequest = { ...request, search, page: 1 };
            setRequest(nextRequest);
            await getPeople(nextRequest);
            setIsSearching(false);
          }}
        />

        <div className={selectionPanelStyles.selectionListFilters}>
          <DropdownSearch
            label="Department"
            options={departmentOptions}
            value={request.departmentSlug}
            responsive
            dialogContained
            onChange={async event => {
              const departmentSlug = event.target.value;
              if (departmentSlug === request.departmentSlug) return;
              setIsLoading(true);
              const nextRequest = { ...request, departmentSlug, page: 1 };
              setRequest(nextRequest);
              await getTeamOptions(nextRequest);
              await getPeople(nextRequest);
              setIsLoading(false);
            }}
          />

          <DropdownSearch
            label="Team"
            options={teamOptions}
            value={request.teamSlug}
            responsive
            dialogContained
            onChange={async event => {
              const teamSlug = event.target.value;
              if (teamSlug === request.teamSlug) return;
              setIsLoading(true);
              const nextRequest = { ...request, teamSlug, page: 1 };
              setRequest(nextRequest);
              await getPeople(nextRequest);
              setIsLoading(false);
            }}
          />

          <DropdownAsync
            label="Roles"
            options={roleOptions}
            value={request.roleSlugs}
            responsive
            dialogContained
            onChange={async event => {
              const roleSlugs = !!event.target.value ? [event.target.value] : [];
              if (roleSlugs.join(',') === request.roleSlugs?.join(',')) return;
              setIsLoading(true);
              const nextRequest = { ...request, roleSlugs, page: 1 };
              setRequest(nextRequest);
              await getPeople(nextRequest);
              setIsLoading(false);
            }}
            selectedOptions={selectedRoleOptions}
            setSelectedOptions={setSelectedRoleOptions}
            isSearching={isSearchingRole}
            onSearch={searchRoleOptions}
          />
        </div>

        {!!people.length ? (
          <React.Fragment>
            <ListLayout gap={12}>
              {people.map(person => {
                const isSelected = selectedEntities.some(selectedEntities => selectedEntities.slug === person.learnerSlug);
                return (
                  <ListItemLayout key={person.learnerSlug}>
                    <CheckboxCard id={person.learnerSlug} checked={isSelected} onChange={() => handlePersonSelection(person, !isSelected)}>
                      <div className={classNames(wizardStyles.assessmentStepListItemGrid, 'card')}>
                        <AssessmentAssigneeListItemLayout assignee={person} />
                      </div>
                    </CheckboxCard>
                  </ListItemLayout>
                );
              })}
            </ListLayout>

            <div className={classNames({ invisible: totalPages <= 1 })}>
              <Pager
                activePageNumber={request.page ?? 1}
                pageCount={totalPages}
                onPageChange={async page => {
                  if (page === request.page) return;
                  setIsLoading(true);
                  const nextRequest = { ...request, page };
                  setRequest(nextRequest);
                  await getPeople(nextRequest);
                  setIsLoading(false);
                }}
              />
            </div>
          </React.Fragment>
        ) : (
          <EmptyState title="No people found" />
        )}
      </div>
    </SkeletonOverlay>
  );
}
